import React from 'react';
import {
  Users,
  CalendarCheck,
  FileText,
  CreditCard,
  Fingerprint,
  MessagesSquare,
} from 'lucide-react';
import useReveal, { reveal } from '../hooks/useReveal';

/*
  Grouped by how much of the buying decision each capability carries, not as a
  flat grid of equals. Cell spans are set per item so the grid has rhythm and
  exactly as many cells as there is content.

  Order matters: the spans below are laid out by grid auto-placement, and they
  are chosen so every row of the three-column grid fills exactly.
    row 1  students (2) + attendance (1)
    row 2  reports (1) + billing (2)
    row 3  communication (2) + staff (1)
  The wide cell alternates side each row, so the grid keeps a rhythm and never
  falls into three equal columns. Adding or removing an entry means
  re-balancing the spans, or the grid will leave a hole.
*/
const CAPABILITIES = [
  {
    id: 'students',
    icon: Users,
    title: 'Students, classes and staff',
    body:
      'Student records, classes, teachers and subjects in one place, organised by term and academic year. Every record stays with the school, not in a spreadsheet on someone’s laptop.',
    span: 'wide',
    tone: 'tint',
  },
  {
    id: 'attendance',
    icon: CalendarCheck,
    title: 'Attendance',
    body: 'Mark attendance digitally and let parents see the days their child was present or absent.',
  },
  {
    id: 'reports',
    icon: FileText,
    title: 'Reports and academics',
    body: 'Build your own report templates, record scores and remarks, then publish reports to parents.',
  },
  {
    id: 'billing',
    icon: CreditCard,
    title: 'Billing and payments',
    body:
      'Create school bills, track balances as they are paid, and issue invoices and receipts. Parents settle fees online instead of bringing cash to the office.',
    span: 'wide',
    tone: 'tint',
  },
  {
    id: 'communication',
    icon: MessagesSquare,
    title: 'Parent communication that stays with the school',
    body:
      'Messages, class announcements and notifications, with images, video, PDFs, replies and reactions. When a teacher leaves, the conversation history does not leave with them.',
    span: 'wide',
    tone: 'tint',
  },
  {
    id: 'staff',
    icon: Fingerprint,
    title: 'Staff management',
    body: 'Control who sees what, and track staff attendance and punching in the same system.',
  },
];

const Features = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section id="features" className="ab-section ab-section--tint ab-caps" ref={ref}>
      <div className="ab-shell">
        <header className={`ab-caps__head ${reveal(isVisible)}`}>
          <h2 className="ab-h2">Everything a school runs on</h2>
          <p className="ab-lead">
            Start with the records and the register. Add billing and parent communication as
            your school is ready for them.
          </p>
        </header>

        <div className="ab-caps__grid">
          {CAPABILITIES.map((item, index) => {
            const Icon = item.icon;
            return (
              <article
                key={item.id}
                className={`ab-caps__cell${item.span ? ` is-${item.span}` : ''}${
                  item.tone === 'tint' ? ' is-tinted' : ''
                } ${reveal(isVisible)}`}
                style={{ '--ab-reveal-delay': `${index * 60}ms` }}
              >
                <span className="ab-caps__icon">
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <h3 className="ab-h3">{item.title}</h3>
                <p className="ab-body">{item.body}</p>
              </article>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        .ab-caps__head { margin-bottom: 2.5rem; }
        .ab-caps__head .ab-lead { margin-top: 0.875rem; }

        .ab-caps__grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1rem;
        }

        .ab-caps__cell {
          display: flex;
          flex-direction: column;
          padding: clamp(1.5rem, 2.4vw, 2rem);
          background: var(--ab-paper);
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-card);
          transition: border-color 0.25s var(--ab-ease), box-shadow 0.25s var(--ab-ease),
            transform 0.25s var(--ab-ease);
        }

        .ab-caps__cell:hover {
          border-color: var(--ab-line-strong);
          box-shadow: var(--ab-shadow-md);
          transform: translateY(-2px);
        }

        .ab-caps__cell.is-wide { grid-column: span 2; }

        /* The three anchor cells sit on a brand tint so the grid is not six identical white boxes. */
        .ab-caps__cell.is-tinted {
          background: linear-gradient(150deg, var(--ab-blue-100) 0%, var(--ab-blue-50) 55%);
          border-color: var(--ab-blue-100);
        }

        .ab-caps__cell.is-tinted .ab-caps__icon {
          background: var(--ab-paper);
        }

        .ab-caps__cell.is-wide .ab-h3 {
          font-size: clamp(1.3rem, 2.1vw, 1.6rem);
        }

        .ab-caps__cell.is-wide .ab-body { max-width: 56ch; }

        .ab-caps__icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          margin-bottom: 1.125rem;
          border-radius: var(--ab-r-inner);
          background: var(--ab-blue-100);
          color: var(--ab-blue);
        }

        .ab-caps__cell .ab-h3 { margin-bottom: 0.5rem; }

        @media (max-width: 940px) {
          .ab-caps__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .ab-caps__cell.is-wide { grid-column: auto; }

          /* Without the extra width, the wide cells drop back to the base scale. */
          .ab-caps__cell.is-wide .ab-h3 { font-size: clamp(1.15rem, 1.7vw, 1.35rem); }
        }

        @media (max-width: 620px) {
          .ab-caps__grid { grid-template-columns: minmax(0, 1fr); }
          .ab-caps__cell.is-wide { grid-column: auto; }
        }
      `}</style>
    </section>
  );
};

export default Features;
